import React, { forwardRef } from 'react';
import { formatCurrency } from '@/lib/utils';

const ReciboImpressao = forwardRef(({ titulo, aluno, escola, responsavel }, ref) => {
  const dataAtual = new Date().toLocaleDateString('pt-BR');
  const horaAtual = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  
  const valorPago = titulo?.paid_value || titulo?.original_value || 0;
  const desconto = titulo?.discount_value || 0;
  const valorOriginal = titulo?.original_value || 0;
  const valorLiquido = titulo?.valor_liquido || valorPago;
  
  const formasPagamento = {
    'dinheiro': 'Dinheiro',
    'pix': 'PIX',
    'cartao_credito': 'Cartão de Crédito',
    'cartao_debito': 'Cartão de Débito',
    'transferencia': 'Transferência Bancária',
    'boleto': 'Boleto Bancário'
  };

  const numeroRecibo = titulo?.id?.slice(-8).toUpperCase() || '00000000';
  
  // Função para converter número em extenso
  const numeroExtenso = (valor) => {
    const unidades = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
    const especiais = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
    const dezenas = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
    const centenas = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];
    
    const inteiro = Math.floor(valor);
    const centavos = Math.round((valor - inteiro) * 100);
    
    let extenso = '';
    
    if (inteiro === 0) {
      extenso = 'zero';
    } else if (inteiro === 100) {
      extenso = 'cem';
    } else if (inteiro < 10) {
      extenso = unidades[inteiro];
    } else if (inteiro < 20) {
      extenso = especiais[inteiro - 10];
    } else if (inteiro < 100) {
      const d = Math.floor(inteiro / 10);
      const u = inteiro % 10;
      extenso = dezenas[d] + (u > 0 ? ' e ' + unidades[u] : '');
    } else if (inteiro < 1000) {
      const c = Math.floor(inteiro / 100);
      const resto = inteiro % 100;
      if (resto === 0) {
        extenso = inteiro === 100 ? 'cem' : centenas[c];
      } else {
        extenso = centenas[c] + ' e ' + numeroExtenso(resto);
      }
    } else if (inteiro < 1000000) {
      const mil = Math.floor(inteiro / 1000);
      const resto = inteiro % 1000;
      if (mil === 1) {
        extenso = 'mil';
      } else {
        extenso = numeroExtenso(mil) + ' mil';
      }
      if (resto > 0) {
        if (resto < 100) {
          extenso += ' e ' + numeroExtenso(resto);
        } else {
          extenso += (resto % 100 === 0 ? ' e ' : ', ') + numeroExtenso(resto);
        }
      }
    }
    
    extenso += ' reais';
    
    if (centavos > 0) {
      extenso += ' e ' + (centavos < 10 ? unidades[centavos] : centavos < 20 ? especiais[centavos - 10] : dezenas[Math.floor(centavos / 10)] + (centavos % 10 > 0 ? ' e ' + unidades[centavos % 10] : '')) + ' centavos';
    }
    
    return extenso;
  };

  return (
    <div ref={ref} className="recibo-impressao bg-white p-8 max-w-[800px] mx-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Primeira Via */}
      <div className="border-2 border-gray-800 p-6 mb-4">
        {/* Cabeçalho */}
        <div className="text-center border-b-2 border-gray-800 pb-4 mb-4">
          <h1 className="text-2xl font-bold uppercase">{escola?.razao_social || 'ESCOLA DNA'}</h1>
          <p className="text-sm">CNPJ: {escola?.cnpj || '00.000.000/0001-00'}</p>
          <p className="text-sm">{escola?.endereco || 'Endereço da Escola'}</p>
          <p className="text-sm">{escola?.cidade || 'Cidade'} - {escola?.uf || 'SP'} | Tel: {escola?.telefone || '(00) 0000-0000'}</p>
        </div>

        {/* Título do Recibo */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold border-2 border-gray-800 inline-block px-8 py-2">
            RECIBO DE PAGAMENTO
          </h2>
          <p className="text-sm mt-2">Nº {numeroRecibo}</p>
        </div>

        {/* Valor em Destaque */}
        <div className="text-center bg-gray-100 py-4 mb-6 border border-gray-400">
          <p className="text-3xl font-bold">{formatCurrency(valorPago)}</p>
          <p className="text-sm italic mt-1">({numeroExtenso(valorPago)})</p>
        </div>

        {/* Dados do Pagamento */}
        <div className="mb-6 space-y-2">
          <p className="text-sm">
            <strong>Recebi de:</strong> {responsavel?.name || aluno?.responsible_name || aluno?.name || 'Responsável'}
          </p>
          <p className="text-sm">
            <strong>CPF/CNPJ:</strong> {responsavel?.cpf || responsavel?.cnpj || aluno?.cpf || '---'}
          </p>
          <p className="text-sm">
            <strong>Aluno(a):</strong> {aluno?.name || 'Aluno'}
          </p>
          <p className="text-sm">
            <strong>Referente a:</strong> {titulo?.description || `Mensalidade ${titulo?.reference_month || ''}`}
          </p>
        </div>

        {/* Detalhes do Pagamento */}
        <div className="border border-gray-400 p-4 mb-6">
          <table className="w-full text-sm">
            <tbody>
              <tr>
                <td className="py-1"><strong>Valor Original:</strong></td>
                <td className="text-right">{formatCurrency(valorOriginal)}</td>
              </tr>
              {desconto > 0 && (
                <tr>
                  <td className="py-1"><strong>Desconto:</strong></td>
                  <td className="text-right text-green-700">- {formatCurrency(desconto)}</td>
                </tr>
              )}
              {titulo?.fine_value > 0 && (
                <tr>
                  <td className="py-1"><strong>Multa:</strong></td>
                  <td className="text-right text-red-700">+ {formatCurrency(titulo.fine_value)}</td>
                </tr>
              )}
              {titulo?.interest_value > 0 && (
                <tr>
                  <td className="py-1"><strong>Juros:</strong></td>
                  <td className="text-right text-red-700">+ {formatCurrency(titulo.interest_value)}</td>
                </tr>
              )}
              <tr className="border-t border-gray-400">
                <td className="py-2"><strong>VALOR PAGO:</strong></td>
                <td className="text-right font-bold text-lg">{formatCurrency(valorPago)}</td>
              </tr>
              {titulo?.taxa_cartao_valor > 0 && (
                <>
                  <tr>
                    <td className="py-1 text-gray-600"><small>Taxa Cartão ({titulo.taxa_cartao_percentual}%):</small></td>
                    <td className="text-right text-gray-600"><small>- {formatCurrency(titulo.taxa_cartao_valor)}</small></td>
                  </tr>
                  <tr>
                    <td className="py-1 text-gray-600"><small>Valor Líquido:</small></td>
                    <td className="text-right text-gray-600"><small>{formatCurrency(valorLiquido)}</small></td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Informações Adicionais */}
        <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
          <div>
            <p><strong>Data do Pagamento:</strong> {titulo?.paid_at ? new Date(titulo.paid_at).toLocaleDateString('pt-BR') : dataAtual}</p>
          </div>
          <div>
            <p><strong>Forma de Pagamento:</strong> {formasPagamento[titulo?.payment_method] || 'Não especificado'}</p>
          </div>
          {titulo?.conta_bancaria && (
            <div className="col-span-2">
              <p><strong>Conta:</strong> {titulo.conta_bancaria.nome} - {titulo.conta_bancaria.banco}</p>
            </div>
          )}
          {titulo?.parcelas_cartao > 1 && (
            <div>
              <p><strong>Parcelas:</strong> {titulo.parcelas_cartao}x</p>
            </div>
          )}
        </div>

        {/* Assinatura */}
        <div className="mt-8 pt-4">
          <div className="flex justify-between items-end">
            <div className="text-sm">
              <p>Emitido em: {dataAtual} às {horaAtual}</p>
              {titulo?.baixa_por_nome && (
                <p className="text-xs text-gray-600">Por: {titulo.baixa_por_nome}</p>
              )}
            </div>
            <div className="text-center">
              <div className="border-t border-gray-800 w-64 pt-2">
                <p className="text-sm">Assinatura / Carimbo</p>
              </div>
            </div>
          </div>
        </div>

        {/* Rodapé */}
        <div className="mt-4 pt-2 border-t border-gray-400 text-center">
          <p className="text-xs text-gray-500">1ª Via - Responsável</p>
        </div>
      </div>

      {/* Linha de Corte */}
      <div className="border-t-2 border-dashed border-gray-400 my-4 relative">
        <span className="absolute left-1/2 -translate-x-1/2 -top-3 bg-white px-4 text-xs text-gray-400">✂ corte aqui</span>
      </div>

      {/* Segunda Via (Escola) */}
      <div className="border-2 border-gray-800 p-6">
        {/* Cabeçalho Compacto */}
        <div className="flex justify-between items-start border-b border-gray-400 pb-3 mb-3">
          <div>
            <h1 className="text-lg font-bold">{escola?.razao_social || 'ESCOLA DNA'}</h1>
            <p className="text-xs">CNPJ: {escola?.cnpj || '00.000.000/0001-00'}</p>
          </div>
          <div className="text-right">
            <p className="font-bold">RECIBO Nº {numeroRecibo}</p>
            <p className="text-xs">2ª Via - Escola</p>
          </div>
        </div>

        {/* Dados Resumidos */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-4">
          <p><strong>Responsável:</strong> {responsavel?.name || aluno?.responsible_name || aluno?.name}</p>
          <p><strong>Aluno:</strong> {aluno?.name}</p>
          <p><strong>Referência:</strong> {titulo?.description || `Mensalidade ${titulo?.reference_month}`}</p>
          <p><strong>Valor:</strong> <span className="font-bold">{formatCurrency(valorPago)}</span></p>
          <p><strong>Data Pgto:</strong> {titulo?.paid_at ? new Date(titulo.paid_at).toLocaleDateString('pt-BR') : dataAtual}</p>
          <p><strong>Forma:</strong> {formasPagamento[titulo?.payment_method] || 'Não especificado'}</p>
        </div>

        {/* Assinatura Compacta */}
        <div className="flex justify-between items-end pt-2 border-t border-gray-400">
          <p className="text-xs text-gray-500">Emitido: {dataAtual} {horaAtual}</p>
          <div className="border-t border-gray-800 w-48 text-center pt-1">
            <p className="text-xs">Assinatura Responsável</p>
          </div>
        </div>
      </div>

      {/* Estilos de Impressão */}
      <style>{`
        @media print {
          .recibo-impressao {
            padding: 0;
            margin: 0;
          }
          @page {
            size: A4;
            margin: 10mm;
          }
        }
      `}</style>
    </div>
  );
});

ReciboImpressao.displayName = 'ReciboImpressao';

export default ReciboImpressao;
